/**
 * @file preset-library.ts
 * @description Библиотека заготовок «Добавить блок» без отрисовки
 * (phase19.16 Р2; uiux-design-r2 §5, §10; SB26, SB27): поиск по названию,
 * «для чего» и ключевым словам с учётом форм слова («кнопка» находит
 * «с кнопкой»), группы со счётчиками, доступность заготовки (подписка —
 * только при работающей рассылке), подпись места вставки.
 */

import { SECTION_PRESET_GROUPS, type SectionPreset, type SectionPresetGroup } from "@chaiberi/shared";

/** Что есть у магазина для условных заготовок. */
export interface PresetFeatures {
  newsletter: boolean;
}

/**
 * Ключевые слова заготовок для поиска (кабинет; `hint` в shared — одна
 * строка «для чего»). Нет записи — ищем по названию и «для чего».
 */
export const PRESET_KEYWORDS: Readonly<Record<string, string>> = {
  "hero-photo-right-bleed": "баннер фото кнопка главная",
  "hero-photo-full": "баннер фото фон акция кнопка",
  "hero-text-center-color": "заголовок страницы цвет текст",
  "hero-photo-left": "фото текст кнопка товар",
  "tiles-3-categories": "категории каталог разделы плитки фото",
  "tiles-4": "ссылки плитки подборки разделы",
  "products-new-with-header": "товары новинки каталог карточки",
  "products-badge": "товары бейдж хиты распродажа бестселлер",
  "promotions-cards": "акции скидки карточки",
  "text-photo-card": "текст фото о нас мастерская",
  "text-photo-plain": "текст фото о нас",
  "cta-messenger": "кнопка телеграм мессенджер написать связаться",
  "cta-color": "кнопка призыв акция каталог",
  "announcements-strip": "объявления строка доставка акция бегущая",
  reviews: "отзывы покупатели доверие",
  faq: "вопросы ответы faq доставка оплата",
  contacts: "контакты телефон адрес почта карта",
  newsletter: "подписка рассылка почта новости",
  "steps-3": "этапы шаги как работаем",
  "team-cards": "команда мастера люди",
  "delivery-payment-3col": "доставка оплата условия",
  "where-to-find": "где купить адрес магазины точки",
  "certificates-gallery": "сертификаты документы галерея фото",
  "about-photo-quote": "о нас цитата фото",
  "questions-left": "вопросы ответы faq",
  "article-featured": "статья блог",
};

/** Почему заготовку нельзя вставить; `null` — можно. */
export function presetBlockedReason(preset: SectionPreset, features: PresetFeatures): string | null {
  if (preset.requires === "newsletter" && !features.newsletter) {
    return "Блок подписки появится, когда у магазина заработает рассылка";
  }
  return null;
}

const normalize = (v: string) => v.toLowerCase().replace(/ё/g, "е");
const words = (v: string) => normalize(v).split(/[^a-zа-я0-9]+/).filter(Boolean);

/**
 * Основа слова для сравнения по началу: длинное слово без двух последних
 * букв, но не короче 4 («кнопка» → «кноп», «отзывы» → «отзы»).
 */
export function wordStem(word: string): string {
  return word.length <= 4 ? word : word.slice(0, Math.max(4, word.length - 2));
}

/** Поиск: каждое слово запроса — начало какого-то слова карточки (с учётом формы). */
export function filterPresets(presets: readonly SectionPreset[], query: string): SectionPreset[] {
  const asked = words(query);
  if (asked.length === 0) return [...presets];
  return presets.filter((p) => {
    const hay = words(`${p.title} ${p.hint} ${PRESET_KEYWORDS[p.key] ?? ""}`);
    return asked.every((w) => {
      const stem = wordStem(w);
      return hay.some((h) => h.startsWith(stem) || h.includes(w));
    });
  });
}

export interface PresetGroupView {
  key: SectionPresetGroup;
  label: string;
  presets: SectionPreset[];
}

/** Группы в порядке библиотеки; пустые (после поиска) отбрасываются. */
export function groupPresets(presets: readonly SectionPreset[]): PresetGroupView[] {
  return SECTION_PRESET_GROUPS.map((g) => ({
    key: g.key,
    label: g.label,
    presets: presets.filter((p) => p.group === g.key),
  })).filter((g) => g.presets.length > 0);
}

/** Текст пустого поиска (uiux-design-r2 §5). */
export const PRESET_SEARCH_EMPTY = "Ничего не нашлось. Попробуйте «фото», «товары», «кнопка» — или возьмите пустой блок.";

/** Подпись места: «после «Три плитки»», «в начало страницы», «в конец страницы». */
export function placeLabel(
  after: string | null | undefined,
  titleOf: (id: string) => { title: string; suffix?: string } | null,
  placeName?: string,
): string {
  // Место блога (над/под лентой, под статьёй) — его имя, а не «страница».
  if (after === null) return placeName ? `${placeName}, первым` : "в начало страницы";
  if (after === undefined) return placeName ?? "в конец страницы";
  const found = titleOf(after);
  return found ? `после «${found.title}»${found.suffix ?? ""}` : "в конец страницы";
}

/**
 * Одинаковые названия блоков для выбора места различаются номером:
 * «после «Секция» (2)», чтобы место не было двусмысленным.
 */
export function numberedTitles<T extends { id: string; title: string }>(blocks: readonly T[]): Array<T & { suffix: string }> {
  const total = new Map<string, number>();
  for (const b of blocks) total.set(b.title, (total.get(b.title) ?? 0) + 1);
  const seen = new Map<string, number>();
  return blocks.map((b) => {
    if ((total.get(b.title) ?? 0) < 2) return { ...b, suffix: "" };
    const n = (seen.get(b.title) ?? 0) + 1;
    seen.set(b.title, n);
    return { ...b, suffix: ` (${n})` };
  });
}
